import { Component } from '@ribajs/core'
import { TwService } from '../modules/tailwind/services'

/**
 * The types for the scope
 */
interface Scope {
  // OPTIONS
  text?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  delay?: number
  trigger?: 'hover' | 'focus' | 'click'

  // STATE
  isVisible: boolean
  tooltipId: string

  // COMPUTED PROPERTIES
  isPositionTop: boolean
  isPositionBottom: boolean
  isPositionLeft: boolean
  isPositionRight: boolean

  // METHODS
  showTooltip: SbTooltipComponent['showTooltip']
  hideTooltip: SbTooltipComponent['hideTooltip']
  toggleTooltip: SbTooltipComponent['toggleTooltip']
}

/**
 * WCAG 2.2 compatible Tooltip component with Live Regions support
 * Usage: <sb-tooltip text="Tooltip content" position="top" trigger="hover">Button text</sb-tooltip>
 */
export class SbTooltipComponent extends Component {
  public static tagName = 'sb-tooltip'

  protected tailwind = TwService.getSingleton()
  private tooltipElement?: HTMLElement
  private triggerElement?: HTMLElement
  private liveRegion?: HTMLElement
  private showTimeout?: number
  private hideTimeout?: number
  private isInitialized = false

  // Bound event handlers to avoid memory leaks
  private readonly boundHandlers = {
    mouseEnter: () => this.handleTriggerEvent('hover', true),
    mouseLeave: () => this.handleTriggerEvent('hover', false),
    focus: () => this.handleTriggerEvent('focus', true),
    blur: () => this.handleTriggerEvent('focus', false),
    click: () => this.handleTriggerEvent('click', null),
    keyDown: (event: KeyboardEvent) => this.handleKeyDown(event),
    resize: () => this.handleResize(),
  }

  public scope: Scope = {
    // OPTIONS
    text: undefined,
    position: 'top',
    delay: 100,
    trigger: 'hover',

    // STATE
    isVisible: false,
    tooltipId: this.generateTooltipId(),

    // COMPUTED PROPERTIES
    isPositionTop: false,
    isPositionBottom: false,
    isPositionLeft: false,
    isPositionRight: false,

    // METHODS
    showTooltip: this.showTooltip.bind(this),
    hideTooltip: this.hideTooltip.bind(this),
    toggleTooltip: this.toggleTooltip.bind(this),
  }

  static get observedAttributes(): string[] {
    return ['text', 'position', 'delay', 'trigger']
  }

  protected requiredAttributes(): string[] {
    return ['text']
  }

  constructor() {
    super()
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbTooltipComponent.observedAttributes)
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.cleanup()
  }

  protected template(): string | null {
    return null // Preserve original content
  }

  async afterBind() {
    await super.afterBind()

    if (!this.isInitialized) {
      this.setupComponent()
      this.isInitialized = true
    }

    this.classList.add(`${SbTooltipComponent.tagName}-ready`)
  }

  private generateTooltipId(): string {
    return `tooltip-${Math.random().toString(36).substr(2, 9)}`
  }

  private setupComponent() {
    this.updatePositionFlags()
    this.setupTriggerElement()
    this.createTooltipElement()
    this.createLiveRegion()
    this.addAllEventListeners()
  }

  private setupTriggerElement() {
    // Prioritize slot element, fall back to first child
    this.triggerElement =
      this.querySelector('[slot="trigger"]') ||
      (this.children[0] as HTMLElement)

    if (!this.triggerElement) {
      console.warn('SbTooltip: No trigger element found')
      return
    }

    this.makeTriggerAccessible()
  }

  private makeTriggerAccessible() {
    if (!this.triggerElement) return

    // Make focusable if not already an interactive element
    const interactiveElements = ['button', 'a', 'input', 'select', 'textarea']
    const tagName = this.triggerElement.tagName.toLowerCase()

    // For SVG elements or non-interactive elements, always add tabindex
    if (
      !this.triggerElement.hasAttribute('tabindex') &&
      (!interactiveElements.includes(tagName) || tagName === 'svg')
    ) {
      this.triggerElement.setAttribute('tabindex', '0')
    }

    // Set ARIA attributes
    this.triggerElement.setAttribute('aria-describedby', this.scope.tooltipId)
    this.triggerElement.setAttribute('aria-expanded', 'false')

    // For better accessibility, add role if it's not an interactive element
    if (!interactiveElements.includes(tagName)) {
      this.triggerElement.setAttribute('role', 'button')
      this.triggerElement.setAttribute(
        'aria-label',
        this.scope.text || 'Information',
      )
    }
  }

  private createTooltipElement() {
    if (this.tooltipElement) {
      this.tooltipElement.remove() // Clean up existing element
    }

    this.tooltipElement = document.createElement('div')
    this.tooltipElement.className = this.getTooltipClasses()
    this.tooltipElement.role = 'tooltip'
    this.tooltipElement.id = this.scope.tooltipId
    this.tooltipElement.textContent = this.scope.text || ''
    this.tooltipElement.style.display = 'none'

    document.body.appendChild(this.tooltipElement)
  }

  private getTooltipClasses(): string {
    const baseClasses =
      'tooltip-content fixed z-50 px-2 py-1 text-sm text-white bg-gray-900 rounded pointer-events-none transition-opacity duration-200 max-w-xs break-words'
    const positionClass = `tooltip-${this.scope.position}`
    return `${baseClasses} ${positionClass}`
  }

  private createLiveRegion() {
    if (document.getElementById('tooltip-live-region')) return

    this.liveRegion = document.createElement('div')
    this.liveRegion.id = 'tooltip-live-region'
    this.liveRegion.setAttribute('aria-live', 'polite')
    this.liveRegion.setAttribute('aria-atomic', 'true')
    this.liveRegion.className =
      'sr-only absolute -top-full opacity-0 pointer-events-none'
    document.body.appendChild(this.liveRegion)
  }

  private addAllEventListeners() {
    this.addTriggerEventListeners()
    this.addGlobalEventListeners()
  }

  private addTriggerEventListeners() {
    if (!this.triggerElement) return

    this.triggerElement.addEventListener(
      'mouseenter',
      this.boundHandlers.mouseEnter,
    )
    this.triggerElement.addEventListener(
      'mouseleave',
      this.boundHandlers.mouseLeave,
    )
    this.triggerElement.addEventListener('focus', this.boundHandlers.focus)
    this.triggerElement.addEventListener('blur', this.boundHandlers.blur)
    this.triggerElement.addEventListener('click', this.boundHandlers.click)
  }

  private addGlobalEventListeners() {
    this.tailwind.on('breakpoint:changed', this.onBreakpointChanged)
    document.addEventListener('keydown', this.boundHandlers.keyDown)
    window.addEventListener('resize', this.boundHandlers.resize)
  }

  private removeTriggerEventListeners() {
    if (!this.triggerElement) return

    this.triggerElement.removeEventListener(
      'mouseenter',
      this.boundHandlers.mouseEnter,
    )
    this.triggerElement.removeEventListener(
      'mouseleave',
      this.boundHandlers.mouseLeave,
    )
    this.triggerElement.removeEventListener('focus', this.boundHandlers.focus)
    this.triggerElement.removeEventListener('blur', this.boundHandlers.blur)
    this.triggerElement.removeEventListener('click', this.boundHandlers.click)
  }

  private removeGlobalEventListeners() {
    this.tailwind.off('breakpoint:changed', this.onBreakpointChanged)
    document.removeEventListener('keydown', this.boundHandlers.keyDown)
    window.removeEventListener('resize', this.boundHandlers.resize)
  }

  private cleanup() {
    this.clearTimeouts()
    this.removeTriggerEventListeners()
    this.removeGlobalEventListeners()

    if (this.tooltipElement) {
      this.tooltipElement.remove()
      this.tooltipElement = undefined
    }
  }

  private clearTimeouts() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
      this.showTimeout = undefined
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = undefined
    }
  }

  // Unified event handler to reduce code duplication
  private handleTriggerEvent(
    eventType: 'hover' | 'focus' | 'click',
    shouldShow: boolean | null,
  ) {
    // For accessibility, focus events should work regardless of trigger setting
    // Click events only work when trigger is set to 'click'
    // Hover events work when trigger is 'hover' (default)
    if (eventType === 'click') {
      if (this.scope.trigger === 'click') {
        this.toggleTooltip()
      }
    } else if (eventType === 'focus') {
      // Focus events should always work for accessibility
      if (shouldShow) {
        this.showTooltip()
      } else {
        this.hideTooltip()
      }
    } else if (eventType === 'hover') {
      // Hover events work when trigger is 'hover' (default)
      if (this.scope.trigger === 'hover') {
        if (shouldShow) {
          this.showTooltip()
        } else {
          this.hideTooltip()
        }
      }
    }
  }

  private updatePositionFlags() {
    const position = this.scope.position || 'top'
    this.scope.isPositionTop = position === 'top'
    this.scope.isPositionBottom = position === 'bottom'
    this.scope.isPositionLeft = position === 'left'
    this.scope.isPositionRight = position === 'right'
  }

  protected async attributeChangedCallback(
    name: string,
    oldValue: any,
    newValue: any,
    namespace: string | null = null,
  ): Promise<void> {
    await super.attributeChangedCallback(name, oldValue, newValue, namespace)

    switch (name) {
      case 'position':
        this.updatePositionFlags()
        if (this.tooltipElement) {
          this.tooltipElement.className = this.getTooltipClasses()
        }
        break
      case 'text':
        if (this.tooltipElement) {
          this.tooltipElement.textContent = newValue || ''
        }
        break
    }
  }

  public showTooltip() {
    this.clearTimeouts()

    this.showTimeout = window.setTimeout(() => {
      this.setTooltipVisible(true)
      this.updateTooltipPosition()
    }, this.scope.delay || 300)
  }

  public hideTooltip() {
    this.clearTimeouts()

    this.hideTimeout = window.setTimeout(() => {
      this.setTooltipVisible(false)
    }, 100)
  }

  public toggleTooltip() {
    if (this.scope.isVisible) {
      this.hideTooltip()
    } else {
      this.showTooltip()
    }
  }

  private setTooltipVisible(visible: boolean) {
    this.scope.isVisible = visible

    if (this.triggerElement) {
      this.triggerElement.setAttribute('aria-expanded', visible.toString())
    }

    if (this.tooltipElement) {
      this.tooltipElement.style.display = visible ? 'block' : 'none'
    }

    // Update live region for screen readers
    if (this.liveRegion) {
      this.liveRegion.textContent = visible ? this.scope.text || '' : ''
    }
  }

  private updateTooltipPosition() {
    if (!this.tooltipElement || !this.triggerElement || !this.scope.isVisible)
      return

    const triggerRect = this.triggerElement.getBoundingClientRect()
    const tooltipRect = this.tooltipElement.getBoundingClientRect()
    const position = this.scope.position || 'top'

    const coords = this.calculateTooltipPosition(
      triggerRect,
      tooltipRect,
      position,
    )
    const constrainedCoords = this.constrainToViewport(coords, tooltipRect)

    this.tooltipElement.style.left = `${constrainedCoords.left}px`
    this.tooltipElement.style.top = `${constrainedCoords.top}px`

    this.updateArrowPosition(triggerRect, constrainedCoords)
  }

  private calculateTooltipPosition(
    triggerRect: DOMRect,
    tooltipRect: DOMRect,
    position: string,
  ): { left: number; top: number } {
    const offset = 8
    const triggerCenterX = triggerRect.left + triggerRect.width / 2
    const triggerCenterY = triggerRect.top + triggerRect.height / 2

    switch (position) {
      case 'top':
        return {
          left: triggerCenterX - tooltipRect.width / 2,
          top: triggerRect.top - tooltipRect.height - offset,
        }
      case 'bottom':
        return {
          left: triggerCenterX - tooltipRect.width / 2,
          top: triggerRect.bottom + offset,
        }
      case 'left':
        return {
          left: triggerRect.left - tooltipRect.width - offset,
          top: triggerCenterY - tooltipRect.height / 2,
        }
      case 'right':
        return {
          left: triggerRect.right + offset,
          top: triggerCenterY - tooltipRect.height / 2,
        }
      default:
        return { left: 0, top: 0 }
    }
  }

  private constrainToViewport(
    coords: { left: number; top: number },
    tooltipRect: DOMRect,
  ): { left: number; top: number } {
    const padding = 8

    return {
      left: Math.max(
        padding,
        Math.min(coords.left, window.innerWidth - tooltipRect.width - padding),
      ),
      top: Math.max(
        padding,
        Math.min(coords.top, window.innerHeight - tooltipRect.height - padding),
      ),
    }
  }

  private updateArrowPosition(
    triggerRect: DOMRect,
    actualPos: { left: number; top: number },
  ) {
    if (!this.tooltipElement) return

    const triggerCenterX =
      triggerRect.left + triggerRect.width / 2 - actualPos.left
    const triggerCenterY =
      triggerRect.top + triggerRect.height / 2 - actualPos.top

    this.tooltipElement.style.setProperty('--arrow-left', `${triggerCenterX}px`)
    this.tooltipElement.style.setProperty('--arrow-top', `${triggerCenterY}px`)
  }

  protected addEventListeners() {
    this.addGlobalEventListeners()
  }

  protected removeEventListeners() {
    this.removeGlobalEventListeners()
  }

  protected onBreakpointChanged() {
    // Handle responsive behavior if needed
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.scope.isVisible) {
      this.hideTooltip()
      this.triggerElement?.focus()
    }
  }

  private handleResize() {
    if (this.scope.isVisible) {
      this.updateTooltipPosition()
    }
  }
}
